import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Link, useRouter } from '@tanstack/react-router'
import { ChevronsUpDown, LogOut, Monitor, Moon, ShieldCheck, Sun } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import type { CurrentAdmin } from '@/shared/auth/permissions'
import { logout } from '@/shared/auth/session'
import { initials } from '@/shared/lib/format'
import { cn } from '@/shared/lib/utils'
import { useTheme, type Theme } from '@/shared/theme/theme-provider'
import { Avatar, AvatarFallback, AvatarImage } from '@/shared/ui/avatar'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/shared/ui/dropdown-menu'

interface SidebarUserProps {
  admin: CurrentAdmin
  collapsed?: boolean
}

/**
 * Rodapé do menu lateral: a seção inteira é o botão de um menu com o tema e o
 * logout.
 */
export function SidebarUser({ admin, collapsed = false }: SidebarUserProps) {
  const { t } = useTranslation()
  const router = useRouter()
  const queryClient = useQueryClient()
  const { theme, setTheme } = useTheme()

  const mutation = useMutation({
    mutationFn: logout,
    onSettled: async () => {
      // Mesmo se a sessão já tiver expirado no servidor, limpa o estado local.
      queryClient.clear()
      await router.navigate({ to: '/login' })
    },
  })

  const role = admin.is_super_admin ? t('dashboard.superAdmin') : (admin.roles[0] ?? t('nav.administrator'))

  return (
    <div className={cn('shrink-0 border-t border-sidebar-border', collapsed ? 'p-2' : 'p-3')}>
      <DropdownMenu>
        <DropdownMenuTrigger
          render={
            <button
              type="button"
              aria-label={t('nav.userMenu')}
              title={collapsed ? admin.name : undefined}
              className={cn(
                'flex w-full items-center gap-2.5 rounded-md py-2 text-left transition-colors outline-none hover:bg-sidebar-accent focus-visible:ring-2 focus-visible:ring-sidebar-ring data-[state=open]:bg-sidebar-accent',
                collapsed ? 'justify-center px-0' : 'px-2',
              )}
            />
          }
        >
          <Avatar className="size-[34px]">
            {admin.avatar_url ? <AvatarImage src={admin.avatar_url} alt="" /> : null}
            <AvatarFallback className="bg-sidebar-primary text-xs font-bold text-sidebar-primary-foreground">
              {initials(admin.name)}
            </AvatarFallback>
          </Avatar>
          {collapsed ? null : (
            <>
              <span className="flex min-w-0 flex-1 flex-col leading-tight">
                <span className="truncate text-[13px] font-bold text-sidebar-accent-foreground">
                  {admin.name}
                </span>
                <span className="truncate text-xs text-sidebar-foreground/80">{role}</span>
              </span>
              <ChevronsUpDown className="size-4 shrink-0 text-sidebar-foreground/80" aria-hidden />
            </>
          )}
        </DropdownMenuTrigger>
        <DropdownMenuContent
          side={collapsed ? 'right' : 'top'}
          align={collapsed ? 'end' : 'center'}
          sideOffset={collapsed ? 10 : 8}
          className={collapsed ? 'w-56' : 'w-(--anchor-width) min-w-56'}
        >
          <DropdownMenuGroup>
            <DropdownMenuLabel className="flex flex-col font-normal">
              <span className="truncate text-sm font-semibold text-foreground">{admin.name}</span>
              <span className="truncate text-xs text-muted-foreground">{admin.email}</span>
            </DropdownMenuLabel>
          </DropdownMenuGroup>
          <DropdownMenuSeparator />
          <DropdownMenuItem render={<Link to="/account" />}>
            <ShieldCheck aria-hidden /> {t('account.menu')}
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuRadioGroup value={theme} onValueChange={(value) => setTheme(value as Theme)}>
            <DropdownMenuLabel className="text-xs text-muted-foreground">
              {t('theme.label')}
            </DropdownMenuLabel>
            <DropdownMenuRadioItem value="light">
              <Sun className="size-4" aria-hidden /> {t('theme.light')}
            </DropdownMenuRadioItem>
            <DropdownMenuRadioItem value="dark">
              <Moon className="size-4" aria-hidden /> {t('theme.dark')}
            </DropdownMenuRadioItem>
            <DropdownMenuRadioItem value="system">
              <Monitor className="size-4" aria-hidden /> {t('theme.system')}
            </DropdownMenuRadioItem>
          </DropdownMenuRadioGroup>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            variant="destructive"
            disabled={mutation.isPending}
            onClick={() => mutation.mutate()}
          >
            <LogOut aria-hidden /> {t('auth.logout')}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  )
}
